import type { FragmentType } from 'src/gql';
import type { Deprecated_HandleItemUpdate } from 'src/types/account';
import { useState } from 'react';
import { EditAssetDialog } from 'src/components/asset/edit-asset-dialog';
import TrackedButton from 'src/components/tracked/tracked-button';
import { graphql, unmaskFragment } from 'src/gql';
import { assetDeleteButtonText } from 'src/utils/asset';

import type { AssetClassName } from '@savvy/constants/generated/gql-enums';
import type { ID } from '@savvy/utils/id';
import { useIsSmallScreen } from '@savvy/deprecated-shared/hooks/use-is-small-screen';
import defaultGuard from '@savvy/utils/default-guard';

import { ViewPortfolioItemDialog } from '../views/view-portfolio-item-dialog';
import PortfolioAssetDescriptionRow from './portfolio-asset-description-row';
import RemoveAssetDialog from './remove-asset-dialog';
import { ViewAssetDialogDetailRows } from './view-asset-dialog-detail-rows';
import { ViewAssetDialogSecondaryAccountDetailRows } from './view-asset-dialog-secondary-account-detail-rows';

export const ViewAssetDialogFragment = graphql(/* GraphQL */ `
  fragment ViewAssetDialog on Asset {
    ...EditAssetDialog
    ...RemoveAssetDialog
    ...PortfolioAssetDescriptionRow
    ...ViewAssetDialogDetailRows
    ...ViewAssetDialogSecondaryAccountDetailRows
    assetClass
  }
`);

function assetHeaderText(assetClass: AssetClassName) {
  switch (assetClass) {
    case 'company_equity':
      return 'Equity details';
    case 'real_estate':
      return 'Real estate details';
    case 'collectible':
    case 'insurance_policy':
    case 'vehicle':
    case 'other':
      return '';
    default:
      return defaultGuard(assetClass, '');
  }
}

export default function ViewAssetDialog({
  assetFrag,
  onEdit,
  onClose,
  clientUserId,
}: {
  readonly assetFrag: FragmentType<typeof ViewAssetDialogFragment>;
  readonly onEdit: Deprecated_HandleItemUpdate;
  readonly onClose: () => void;
  readonly clientUserId?: ID;
}) {
  const asset = unmaskFragment(ViewAssetDialogFragment, assetFrag);
  const [isEditing, setIsEditing] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const isSmallScreen = useIsSmallScreen();

  if (isEditing) {
    return (
      <EditAssetDialog
        assetFrag={asset}
        onClose={() => {
          setIsEditing(false);
        }}
        onBack={() => {
          setIsEditing(false);
        }}
        onEdit={onEdit}
        clientUserId={clientUserId}
      />
    );
  } else if (isRemoving) {
    return (
      <RemoveAssetDialog
        assetFrag={asset}
        onClose={() => {
          setIsRemoving(false);
        }}
        onRemove={onEdit}
      />
    );
  }

  return (
    <ViewPortfolioItemDialog
      deleteButton={
        <TrackedButton
          elementName="view-asset-dialog-delete-button"
          variant="secondary"
          onClick={() => {
            setIsRemoving(true);
          }}
          className={isSmallScreen ? 'w-[156px]' : undefined}
        >
          {assetDeleteButtonText(asset.assetClass)}
        </TrackedButton>
      }
      descriptionRow={<PortfolioAssetDescriptionRow assetFrag={asset} />}
      detailRows={<ViewAssetDialogDetailRows assetFrag={asset} />}
      editButton={
        <TrackedButton
          elementName="view-asset-dialog-edit-button"
          variant="secondary"
          onClick={() => {
            setIsEditing(true);
          }}
          className={isSmallScreen ? 'w-[156px]' : undefined}
        >
          Edit details
        </TrackedButton>
      }
      headerText={assetHeaderText(asset.assetClass)}
      secondaryDetailRows={<ViewAssetDialogSecondaryAccountDetailRows assetFrag={asset} />}
      onClose={onClose}
    />
  );
}
